import { Box, Divider, Flex, Image, Skeleton } from '@chakra-ui/react';
import { Card } from '@mesh.ai/rabbit';
import { useGetCheckInByInstanceId } from 'Deer/pages/MyData/CheckIns/data';
import {
  CheckInsHistoryInstance,
  CHECK_INS_STATUS_COLORS,
  CHECK_INS_STATUS_LABELS,
  CHECK_INS_STATUS_VALUES,
} from 'Deer/pages/MyData/CheckIns/types';

import checkInIncomplete from 'assets/assetsV1/illustrations/check-in.svg';

import { RichTextEditor2 } from 'uiToolkitV1/RichTextEditor2';
import { TagColored } from 'uiToolkitV1/Tag';
import { TextBodyMedium, TextHeader3, TextHeader4 } from 'uiToolkitV1/Typography';

const MissedCheckin = () => (
  <Flex flexDir="column" alignItems="center" justifyContent="center" h="300px" gap="16px">
    <Image src={checkInIncomplete as unknown as string} alt="check-in-incomplete" />
    <Box textAlign="center">
      <TextHeader3 bold>You missed the checkin deadline.</TextHeader3>
      <TextBodyMedium color="text.g2">
        Incomplete check-ins can&apos;t be filled anymore due to time lapse.
      </TextBodyMedium>
    </Box>
  </Flex>
);

type PastCheckInIntentProps = {
  selectedCheckIn: CheckInsHistoryInstance | null;
};

export const PastCheckInIntent = ({ selectedCheckIn }: PastCheckInIntentProps) => {
  const instanceId = selectedCheckIn?.checkinUpdateInstanceId || null;
  const { data: checkInInstanceDetails, isLoading, isError } = useGetCheckInByInstanceId(instanceId);

  if (!selectedCheckIn || isError) return null;

  if (isLoading)
    return (
      <Card style={{ height: 'calc(100vh - 164px)', padding: 0 }}>
        <Box w="100%" p="18px">
          <Skeleton w="171px" h="20px" />
          <Divider h="1px" w="100%" bg="#E7E7E7" mt="8px" />
          <Flex flexDir="column" gap="12px" mt="24px">
            {[1, 2, 3].map((question) => (
              <Flex key={question} flexDir="column" gap="4px">
                <Skeleton w="100%" h="20px" />
                <Skeleton w="100%" h="120px" />
              </Flex>
            ))}
          </Flex>
        </Box>
      </Card>
    );

  const isMissedCheckIn = checkInInstanceDetails?.status === CHECK_INS_STATUS_VALUES.PENDING;

  return (
    <Card style={{ height: 'calc(100vh - 164px)', padding: 0 }}>
      <Flex flexDir="column" justifyContent="space-between" h="100%" w="100%">
        <Box p="18px">
          <TextHeader4 bold>Your check-in responses</TextHeader4>
          <Divider h="1px" w="100%" bg="#E7E7E7" mt="8px" />
          {!isMissedCheckIn && checkInInstanceDetails && (
            <Flex flexDir="column" gap="12px" mt="24px">
              {checkInInstanceDetails.questions.map((question) => (
                <Flex key={question.id} flexDir="column" gap="4px">
                  <TextHeader4 bold>{`Q ${question.description}`}</TextHeader4>
                  <RichTextEditor2
                    content={question.responses[0].checkinUpdateTextHtml}
                    disabled
                    showMenuBar={false}
                    maxCharLimit={5000}
                    onChange={() => {}}
                    onOutsideClick={() => {}}
                    containerProps={{ border: '0 !important' }}
                  />
                </Flex>
              ))}
            </Flex>
          )}
          {isMissedCheckIn && <MissedCheckin />}
        </Box>
        <Flex
          alignItems="center"
          justifyContent="end"
          h="52px"
          w="100%"
          bg="neutral.F5F7F9"
          borderBottomLeftRadius="0.5rem"
          borderBottomEndRadius="0.5rem"
          p="16px"
        >
          <TagColored color={CHECK_INS_STATUS_COLORS[checkInInstanceDetails.status]}>
            {CHECK_INS_STATUS_LABELS[checkInInstanceDetails.status]}
          </TagColored>
        </Flex>
      </Flex>
    </Card>
  );
};
